import { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { SRangeSlider, SText } from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';
import { ProductFilterInterface } from 'pages/products/hooks/use-ai-search-result';
import { TextColor } from '@setvi/shared/enums';
import { ProductFilter } from './filter';
import { CheckIcon, ListContainer, ListItem, ListItemLoader } from './styled';

interface AIProductsFiltersProducts {
  products: Product[];
  selectedFilters: ProductFilterInterface;
  showStockFilter?: boolean;
  showPriceFilter?: boolean;
  loading?: boolean;
  onChange?: React.Dispatch<React.SetStateAction<ProductFilterInterface>>;
}

export const AIProductsFilters = ({
  products,
  selectedFilters,
  showStockFilter,
  showPriceFilter = true,
  loading,
  onChange
}: AIProductsFiltersProducts) => {
  const showCostFilter = useMemo(
    () => products.some(i => i.cost !== undefined) && showPriceFilter,
    [products, showPriceFilter]
  );
  const minCost = Math.floor(
    products?.reduce(
      (min, item) => (item?.cost < min ? item?.cost : min),
      products?.[0]?.cost || 0
    )
  );
  const maxCost = Math.ceil(
    products?.reduce(
      (max, item) => (item?.cost > max ? item?.cost : max),
      products?.[0]?.cost || 0
    )
  );

  const removeDuplicatesById = (
    arr: {
      name: string;
      manufacturerSku: string;
    }[]
  ) => {
    const unique = new Set();

    return arr.filter(item => {
      const key = `${item.name}`;
      if (!unique.has(key)) {
        unique.add(key);
        return true;
      }
      return false;
    });
  };

  const manufacturers = removeDuplicatesById(
    products.map(i => ({
      name: i.vendor.name,
      manufacturerSku: i.manufacturerSku
    }))
  );

  return (
    <Box display="flex" gridGap="16px">
      <ProductFilter
        anchorWidth={200}
        title={
          selectedFilters.manufacturer.length
            ? selectedFilters.manufacturer
                .map(
                  i => products.find(j => j?.vendor?.name === i)?.vendor?.name
                )
                .join(', ')
            : 'Manufacturer'
        }
        fontColor={selectedFilters.manufacturer.length ? TextColor.Blue : null}
        label="By Manufacturer"
        clearAll={
          selectedFilters?.manufacturer?.length
            ? () => onChange(prev => ({ ...prev, manufacturer: [] }))
            : null
        }
        dropdownContent={
          <ListContainer>
            {manufacturers.map(({ name }) => {
              const isSelected = selectedFilters.manufacturer.includes(name);

              return (
                <ListItem
                  onClick={() =>
                    onChange(prev => ({
                      ...prev,
                      manufacturer: isSelected
                        ? prev.manufacturer.filter(i => i !== name)
                        : [...prev.manufacturer, name]
                    }))
                  }>
                  <Box>
                    <SText
                      title={name}
                      size="sm"
                      lineHeight="20px"
                      fontColor={isSelected && TextColor.Blue}
                    />
                    {/* manufacturerSku && (
                      <SText
                        title={manufacturerSku}
                        size="xs"
                        lineHeight="16px"
                        fontColor={TextColor.Grey}
                      />
                    ) */}
                  </Box>
                  {isSelected && <CheckIcon />}
                </ListItem>
              );
            })}
            {loading &&
              Array.from({ length: 5 }).map(() => (
                <ListItemLoader height={60} />
              ))}
          </ListContainer>
        }
      />
      {showStockFilter && (
        <ProductFilter
          anchorWidth={200}
          fontColor={selectedFilters.stock !== null ? TextColor.Blue : null}
          title={
            selectedFilters.stock === null
              ? 'Stock Status'
              : `${selectedFilters.stock ? 'In Stock' : 'Out of Stock'}`
          }
          clearAll={
            selectedFilters?.stock !== null
              ? () => onChange(prev => ({ ...prev, stock: null }))
              : null
          }
          clearTitle="Clear"
          label="Stock Status"
          dropdownContent={
            <ListContainer>
              {!loading ? (
                <>
                  <ListItem
                    onClick={() =>
                      onChange(prev => ({
                        ...prev,
                        stock: prev.stock === true ? null : true
                      }))
                    }>
                    <SText
                      title="In Stock"
                      size="sm"
                      lineHeight="20px"
                      fontColor={!!selectedFilters.stock && TextColor.Blue}
                    />
                    {!!selectedFilters.stock && <CheckIcon />}
                  </ListItem>
                  <ListItem
                    onClick={() =>
                      onChange(prev => ({
                        ...prev,
                        stock: prev.stock === false ? null : false
                      }))
                    }>
                    <SText
                      title="Out of Stock"
                      size="sm"
                      lineHeight="20px"
                      fontColor={
                        selectedFilters.stock === false && TextColor.Blue
                      }
                    />
                    {selectedFilters.stock === false && <CheckIcon />}
                  </ListItem>
                </>
              ) : (
                <>
                  <ListItemLoader height={44} />
                  <ListItemLoader height={44} />
                </>
              )}
            </ListContainer>
          }
        />
      )}
      {showCostFilter && (
        <ProductFilter
          title={
            selectedFilters.cost.length &&
            (selectedFilters.cost[1] !== maxCost ||
              selectedFilters.cost[0] !== minCost)
              ? `$${selectedFilters.cost[0]}-$${selectedFilters.cost[1]}`
              : 'Select Price'
          }
          anchorWidth={200}
          fontColor={
            selectedFilters.cost.length &&
            (selectedFilters.cost[1] !== maxCost ||
              selectedFilters.cost[0] !== minCost)
              ? TextColor.Blue
              : null
          }
          width={260}
          disabled={loading || !products.length}
          dropdownContent={
            <div
              onClick={e => e.stopPropagation()}
              style={{
                marginTop: 8
              }}>
              <SRangeSlider
                label="Price Range"
                min={minCost}
                max={maxCost}
                defaultRange={selectedFilters.cost as [number, number]}
                onChange={values =>
                  onChange(prev => ({ ...prev, cost: values }))
                }
              />
            </div>
          }
        />
      )}
    </Box>
  );
};
